import { useCallback, useState } from 'preact/hooks';
import { usePaginatedList } from '../../hooks/use-paginated-list';
import { useAsyncAction } from '../../hooks/use-async-action';
import { getProducts, exportProducts, updateProduct } from '../../api/products';
import { Card } from '../../components/card';
import { Table, type Column } from '../../components/table';
import { Pagination } from '../../components/pagination';
import { Input } from '../../components/input';
import { Button } from '../../components/button';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { NutriscoreBadge } from '../../components/nutriscore-badge';
import { ViewToggle } from '../../components/view-toggle';
import { ProductCardGrid } from './product-card-grid';
import { BulkDelete } from './bulk-delete';
import type { Product } from '../../api/types';
import type { JSX } from 'preact';

interface ProductTableProps {
  onSelect: (barcode: string) => void;
}

const LIMIT = 20;

const VIEW_MODE_KEY = 'sift-products-view-mode';

function getInitialViewMode(): 'table' | 'cards' {
  try {
    const stored = sessionStorage.getItem(VIEW_MODE_KEY);
    if (stored === 'cards') return 'cards';
  } catch { /* ignore */ }
  return 'table';
}

function InlineEditCell({ value, barcode, field, onSaved }: { value: string | null; barcode: string; field: 'name' | 'brand'; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (editValue === (value ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateProduct(barcode, { [field]: editValue });
      onSaved();
      setEditing(false);
    } catch {
      // revert
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditing(false); setEditValue(value ?? ''); }
  };

  if (editing) {
    return (
      <input
        class="cell-editable-input"
        type="text"
        value={editValue}
        onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => setEditValue(e.currentTarget.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={saving}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onClick={(e: Event) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      class="cell-editable"
      onClick={(e: Event) => {
        e.stopPropagation();
        setEditing(true);
        setEditValue(value ?? '');
      }}
      title="Click to edit"
    >
      {value || '\u2014'}
    </span>
  );
}

export function ProductTable({ onSelect }: ProductTableProps) {
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(getInitialViewMode);

  const handleViewModeChange = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    try { sessionStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* ignore */ }
  };

  const fetcher = useCallback(async (page: number, search: string) => {
    const res = await getProducts({ page, limit: LIMIT, search: search || undefined });
    return { items: res.products, total: res.total };
  }, []);

  const list = usePaginatedList<Product>(fetcher);

  const csvExport = useAsyncAction(async () => {
    const blob = await exportProducts();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

  const columns: Column<Product>[] = [
    {
      header: '',
      key: 'image_url',
      render: (r) => (
        <div style={{ width: '32px', height: '32px', borderRadius: 'var(--radius-xs)', background: 'var(--bg-surface)', overflow: 'hidden', flexShrink: 0 }}>
          {r.image_url && (
            <img
              src={r.image_url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              loading="lazy"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      ),
    },
    { header: 'Barcode', key: 'barcode', sortable: true },
    {
      header: 'Name',
      key: 'name',
      sortable: true,
      render: (r) => (
        <InlineEditCell
          value={r.name}
          barcode={r.barcode}
          field="name"
          onSaved={list.refresh}
        />
      ),
    },
    {
      header: 'Brand',
      key: 'brand',
      sortable: true,
      render: (r) => (
        <InlineEditCell
          value={r.brand}
          barcode={r.barcode}
          field="brand"
          onSaved={list.refresh}
        />
      ),
    },
    {
      header: 'Nutriscore',
      key: 'nutriscore_grade',
      render: (r) => <NutriscoreBadge grade={r.nutriscore_grade} />,
    },
    { header: 'Source', key: 'source', render: (r) => r.source ?? '\u2014' },
    { header: 'Scans', key: 'scan_count', sortable: true },
  ];

  return (
    <div>
      <Card>
        <div class="toolbar">
          <Input
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search by barcode, name, or brand..."
            icon="search"
          />
          <ViewToggle mode={viewMode} onChange={handleViewModeChange} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => csvExport.execute()}
            disabled={csvExport.loading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style={{ width: '13px', height: '13px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {csvExport.loading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>

        {csvExport.error && <Message text={csvExport.error} type="error" />}

        {list.loading ? (
          <Spinner text="Loading products" centered />
        ) : list.error ? (
          <Message text={list.error} type="error" />
        ) : list.items.length === 0 ? (
          <EmptyState icon="search" message="No products found" />
        ) : viewMode === 'cards' ? (
          <>
            <ProductCardGrid products={list.items} onSelect={onSelect} />
            <Pagination
              page={list.page}
              total={list.total}
              limit={LIMIT}
              onPageChange={list.setPage}
            />
          </>
        ) : (
          <>
            <Table<Record<string, unknown>>
              columns={columns as unknown as Column<Record<string, unknown>>[]}
              rows={list.items as unknown as Record<string, unknown>[]}
              onRowClick={(row) => onSelect(row.barcode as string)}
            />
            <Pagination
              page={list.page}
              total={list.total}
              limit={LIMIT}
              onPageChange={list.setPage}
            />
          </>
        )}
      </Card>

      <BulkDelete onComplete={list.refresh} />
    </div>
  );
}
